import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, DirectionsRenderer } from '@react-google-maps/api';
import { useMaps } from './MapsProvider.jsx';
import LeafletRouteMap from './LeafletRouteMap.jsx';

function RouteMap({ origin, destination, showDefaultMap, height, style }) {
  // Use provided style height first, then height prop, then container will control
  const containerStyle = {
    width: '100%',
    maxWidth: '100%',
    minWidth: '250px', // Ensure minimum reasonable width for mobile
    height: style?.height || (height ? `${height}px` : '100%'), // Respect style height first
    borderRadius: '12px',
    marginTop: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
    ...style,
  };
  const { isLoaded, loadError, authError } = useMaps();
  const [directions, setDirections] = useState(null);
  const [error, setError] = useState(null);
  const [authFailed, setAuthFailed] = useState(false);
  const [loaderTimedOut, setLoaderTimedOut] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [containerRef, setContainerRef] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const isDev = import.meta.env.MODE !== 'production';
  const attemptRef = useRef(0);
  const directionsRef = useRef(null);

  // Dev logging to confirm component mounts and props/state flow
  useEffect(() => {
    if (!isDev) return;
    // eslint-disable-next-line no-console
    console.log('[RouteMap] mount');
    return () => {
      // eslint-disable-next-line no-console
      console.log('[RouteMap] unmount');
    };
  }, [isDev]);

  useEffect(() => {
    if (!isDev) return;
    // eslint-disable-next-line no-console
    console.log('[RouteMap] props/state', { origin, destination, isLoaded, loadError, authFailed });
  }, [origin, destination, isLoaded, loadError, authFailed, isDev]);

  // Detect Google Maps auth failures (e.g., ExpiredKeyMapError)
  useEffect(() => {
    const handler = () => setAuthFailed(true);
    // Google Maps JS API invokes window.gm_authFailure on auth errors
    window.gm_authFailure = handler;
    return () => {
      if (window.gm_authFailure === handler) {
        delete window.gm_authFailure;
      }
    };
  }, []);

  // Force map resize when container dimensions change
  useEffect(() => {
    if (mapInstance && window.google && window.google.maps) {
      const timer = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance, 'resize');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mapInstance, origin, destination]);

  // Additional resize trigger on initial load to handle layout establishment
  useEffect(() => {
    if (mapInstance && window.google && window.google.maps) {
      const timer = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance, 'resize');
      }, 500); // Slightly longer delay for initial grid layout
      return () => clearTimeout(timer);
    }
  }, [mapInstance]);

  // ResizeObserver to monitor container size changes
  useEffect(() => {
    if (!containerRef || !mapInstance || !window.google?.maps) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Debounce the resize trigger
        setTimeout(() => {
          if (mapInstance && window.google?.maps) {
            window.google.maps.event.trigger(mapInstance, 'resize');
          }
        }, 50);
      }
    });

    resizeObserver.observe(containerRef);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, mapInstance]);

  // Fit map to route bounds when available
  useEffect(() => {
    if (mapInstance && mapBounds && window.google?.maps) {
      const timer = setTimeout(() => {
        try {
          mapInstance.fitBounds(mapBounds);
          // Add some padding to the bounds
          const padding = { top: 20, right: 20, bottom: 20, left: 20 };
          mapInstance.fitBounds(mapBounds, padding);
        } catch (error) {
          console.warn('Error fitting bounds:', error);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mapInstance, mapBounds]);

  // If Google loader never finishes, fallback after a grace period
  useEffect(() => {
    if (isLoaded) return; // loaded fine
    setLoaderTimedOut(false);
    const t = setTimeout(() => {
      if (!window.google || !window.google.maps) {
        setLoaderTimedOut(true);
      }
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useEffect(() => {
    let isMounted = true;
    const isString = v => typeof v === 'string';
    const isValid = v => isString(v) && v.trim().length > 0;
    const getAddressString = v => {
      if (isString(v)) return v;
      if (v && typeof v === 'object' && v.formatted_address) return v.formatted_address;
      return '';
    };

    if (!isLoaded || loadError || authFailed || authError) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[RouteMap] skipping route: ', { isLoaded, loadError, authFailed, authError });
      }
      return; // Wait or fallback handled below
    }

  const originStr = getAddressString(origin);
  const destinationStr = getAddressString(destination);

    if (window.google && window.google.maps) {
      // Accept full addresses as well as valid city/province and postal-code inputs.
      // The quote flow intentionally allows users to start with cities.
      const looksLikeAddress = (s) => s.length >= 5 && (s.includes(',') || /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i.test(s));
      if (!isValid(originStr) || !isValid(destinationStr) || !looksLikeAddress(originStr) || !looksLikeAddress(destinationStr)) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('[RouteMap] missing/incomplete addresses; clearing', { originStr, destinationStr });
        }
        setDirections(null);
        setMapBounds(null);
        setError(null);
        return;
      }
      // Start/advance attempt counter so a concurrent timeout knows if it’s stale
      const thisAttempt = ++attemptRef.current;
      const run = () => {
        if (!isMounted) return;
        setIsRouting(true);
        // eslint-disable-next-line no-console
        console.log('[RouteMap] Requesting directions', { origin: originStr, destination: destinationStr });
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: originStr,
            destination: destinationStr,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (!isMounted) return;
            // eslint-disable-next-line no-console
            console.log('[RouteMap] DirectionsService status:', status, result);
            if (status === 'OK') {
              directionsRef.current = result;
              setDirections(result);
              setError(null);
              
              // Calculate and store the bounds for the route
              if (result && result.routes && result.routes[0]) {
                setMapBounds(result.routes[0].bounds);
              }
              
              // Cancel any pending fallback timer for this attempt
              try { clearTimeout(fallbackTid); } catch {}
            } else {
              directionsRef.current = null;
              setDirections(null);
              setMapBounds(null);
              // Suppress transient errors while user is still typing incomplete addresses
              if (status === 'ZERO_RESULTS' || status === 'NOT_FOUND') {
                setError(null);
              } else {
                setError(`DirectionsService error: ${status}`);
                console.error('DirectionsService error:', status, result);
              }
            }
            setIsRouting(false);
          }
        );
      };
      // Debounce slightly to avoid hammering Directions API while typing
      const tid = setTimeout(run, 300);
      // Fallback timer: if after 2500ms we still have no directions and this is the latest attempt, trigger fallback
      const fallbackTid = setTimeout(() => {
        if (thisAttempt === attemptRef.current && !directionsRef.current) {
          if (isDev) {
            // eslint-disable-next-line no-console
            console.warn('[RouteMap] directions timeout; triggering fallback');
          }
          setError('Directions timeout');
        }
      }, 2500);

      return () => {
        clearTimeout(tid);
        clearTimeout(fallbackTid);
      };
    } else if (showDefaultMap) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[RouteMap] google maps not ready on window');
      }
      setDirections(null);
      setError(null);
    }

    return () => {
      isMounted = false;
    };
  }, [origin, destination, showDefaultMap, isLoaded, loadError, authFailed, authError]);

  // Use Leaflet/OSM as fallback when Google Maps is unavailable.
  if (loadError || authFailed || authError || loaderTimedOut) {
    return (
      <LeafletRouteMap
        origin={origin}
        destination={destination}
        showDefaultMap={showDefaultMap}
        height={height}
        style={style}
      />
    );
  }

  if (!isLoaded) return <div style={{ padding: 12 }}>Loading map...</div>;

  return (
    <div 
      ref={setContainerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapBounds ? undefined : { lat: 53.7267, lng: -127.6476 }} // Only use default center if no bounds
        zoom={mapBounds ? undefined : (origin && destination ? 5 : 4)} // Only use default zoom if no bounds
        options={{ 
          disableDefaultUI: true,
          gestureHandling: 'cooperative', // Better for mobile scrolling
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
        onLoad={(map) => {
          setMapInstance(map);
          // If we already have bounds when the map loads, fit to them
          if (mapBounds) {
            setTimeout(() => {
              try {
                const padding = { top: 20, right: 20, bottom: 20, left: 20 };
                map.fitBounds(mapBounds, padding);
              } catch (error) {
                console.warn('Error fitting bounds on load:', error);
              }
            }, 100);
          }
        }}
      >
        {directions && <DirectionsRenderer directions={directions} />}
      </GoogleMap>
      {isDev && (
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '6px 8px', borderRadius: 6, fontSize: 11, maxWidth: '90%' }}>
          <div><strong>Origin:</strong> {typeof origin === 'string' ? origin : ''}</div>
          <div><strong>Destination:</strong> {typeof destination === 'string' ? destination : ''}</div>
        </div>
      )}
      {(!origin || !destination) && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(255,255,255,0.8)', padding: 8, borderRadius: 8, fontSize: 12 }}>
            Enter both addresses to see the route.
          </div>
        </div>
      )}
      {(isRouting || error) && (
        <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(255,255,255,0.9)', padding: '6px 8px', borderRadius: 6, fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {isRouting ? 'Calculating route…' : error}
        </div>
      )}
    </div>
  );
}

export default RouteMap;
